import { numberFixDecimals } from '@local_modules/generic/numbers/number-fix-decimals/index.js'
import { sum } from 'd3'
import dayjs from 'dayjs'

/**
 * Status:
 * - 0 - All Good: all matching
 * - 1 - All Good: the only mismatch are FX individual lines
 * - 2 - All Good, except FX, for current month (ie FX is revaluated constantly in Xero)
 * - 10 - Issue in values which should match

 * @param {*} containerElementId
 * @param {*} data
 * @returns
 */
export function showSummary (containerElementId, loadedData) {
  // console.log('showSummary', loadedData)

  const currentMonthISO = dayjs().format('YYYY-MM')
  const isCurrentMonth = loadedData.monthISO === currentMonthISO
  // console.log('isCurrentMonth', isCurrentMonth, loadedData.monthISO, new Date().toISOString().substring(0, 7))

  const rowsData = loadedData.rows

  const dataValuesByCase = rollupByCase(rowsData)
  // console.log('dataValuesByCase', dataValuesByCase)

  const summary = {
    dataMatchCase: null,
    issueCode: null,
    dataValuesByCase,
    monthISO: loadedData.monthISO
  }

  // const varianceTotal = numberFixDecimals(sum(rowsData, d => d.amountXero) - sum(rowsData, d => d.amountDb), 2)

  if (dataValuesByCase.notMatching?.all?.groupValues?.length === 0) {
    summary.issueCode = 0
    summary.dataMatchCase = 'All good'
    summary.dataMatchDetails = 'All accounts lines are matching'
  } else if (isCurrentMonth && dataValuesByCase.notMatching?.notFx?.groupValues?.length === 0) {
    summary.issueCode = 1
    summary.dataMatchCase = 'Good: Only current month FX revaluations have variance'
    summary.dataMatchDetails = 'The variance between zenflow and Xero is due to Xero FX revaluation since latest data sync'
  } else if (dataValuesByCase.notMatching?.notFx?.groupValues?.length === 0) {
    summary.issueCode = 2
    summary.dataMatchCase = 'Good'
    summary.dataMatchDetails = 'While individual FX revaluations have variance, they offset each other as expected'
  } else {
    summary.issueCode = 10
    summary.dataMatchCase = 'Something is wrong'
    summary.dataMatchCase = 'There are issues in values which should match'
  }

  console.log('summary', summary)
  const messageHtml = `
  <article class="message ${summary.issueCode === 10 ? 'is-warning' : 'is-success'}">
    <div class="message-header">
      <p>[${summary.issueCode}] ${summary.monthISO}: ${summary.dataMatchCase}: ${summary.dataMatchDetails}</p>
    </div>
    <div class="message-body">
      Number of values:
      <table class="table is-striped">
        <thead>
          <tr>
            <th>Case</th>
            <th>Matching</th>
            <th>Not Matching</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>All</td>
            <td>${dataValuesByCase.matching.all.groupValues?.length}</td>
            <td>${dataValuesByCase.notMatching.all.groupValues?.length}</td>
            <td>${dataValuesByCase.all.all.groupValues?.length}</td>
          </tr>
          <tr>
            <td>Not FX Revaluations</td>
            <td>${dataValuesByCase.matching.notFx.groupValues?.length}</td>
            <td>${dataValuesByCase.notMatching.notFx.groupValues?.length}</td>
            <td>${dataValuesByCase.all.notFx.groupValues?.length}</td>
          </tr>
          <tr>
            <td>FX Revaluations</td>
            <td>${dataValuesByCase.matching.isFx.groupValues?.length}</td>
            <td>${dataValuesByCase.notMatching.isFx.groupValues?.length}</td>
            <td>${dataValuesByCase.all.isFx.groupValues?.length}</td>
          </tr>
        </tbody>
      </table>

      Variance (zenflow - xero) of values:
      <table class="table is-striped">
        <thead>
          <tr>
            <th>Case</th>
            <th>Matching</th>
            <th>Not Matching</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>All</td>
            <td>${dataValuesByCase.matching.all.variance}</td>
            <td>${dataValuesByCase.notMatching.all.variance}</td>
            <td>${dataValuesByCase.all.all.variance}</td>
          </tr>
          <tr>
            <td>Not FX Revaluations</td>
            <td>${dataValuesByCase.matching.notFx.variance}</td>
            <td>${dataValuesByCase.notMatching.notFx.variance}</td>
            <td>${dataValuesByCase.all.notFx.variance}</td>
          </tr>
          <tr>
            <td>FX Revaluations</td>
            <td>${dataValuesByCase.matching.isFx.variance}</td>
            <td>${dataValuesByCase.notMatching.isFx.variance}</td>
            <td>${dataValuesByCase.all.isFx.variance}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </article>
  `

  document.getElementById(containerElementId).innerHTML = messageHtml

  return summary
}

/**
 *
 * @param {*} rowsData
 * @returns {Object} Indexed: isMatching.isFx
 */
function rollupByCase (rowsData) {
  function rollupFunction (groupValues) {
    const amountXero = numberFixDecimals(sum(groupValues, d => d.amountXero), 2)
    const amountDb = numberFixDecimals(sum(groupValues, d => d.amountDb), 2)
    const variance = numberFixDecimals(amountXero - amountDb, 2)
    return {
      groupValues,
      amountXero,
      amountDb,
      variance
    }
  }

  function grouping (allValues, isMatching, isFx) {
    const groupValues = allValues.filter(d =>
      (isMatching === 'all' || d.isMatching === isMatching) &&
      (isFx === 'all' || d.isFx === isFx))
    const rollup = rollupFunction(groupValues)
    return rollup
  }

  const rollup = {
    matching: {},
    notMatching: {},
    all: {}
  }

  rollup.matching.isFx = grouping(rowsData, true, true)
  rollup.matching.notFx = grouping(rowsData, true, false)
  rollup.matching.all = grouping(rowsData, true, 'all')
  rollup.notMatching.isFx = grouping(rowsData, false, true)
  rollup.notMatching.notFx = grouping(rowsData, false, false)
  rollup.notMatching.all = grouping(rowsData, false, 'all')
  rollup.all.isFx = grouping(rowsData, 'all', true)
  rollup.all.notFx = grouping(rowsData, 'all', false)
  rollup.all.all = grouping(rowsData, 'all', 'all')
  // console.log('rollup', rollup)

  return rollup
}
